"use client";

/**
 * Conversion events — the funnel stages past a CTA click.
 *
 * Feeds the Conversions tab, funnel drop-off, conversion paths,
 * time-to-conversion, and the Sessions "Converted" card. All of those read
 * signup_events.conversion_type, and all of them were structurally zero because
 * nothing wrote the table.
 *
 * The type values are not free-form: they are CONVERSION_TYPE_IDS from
 * src/lib/trackingTaxonomy.ts, which also decides each one's tier (Primary or
 * Secondary). /api/track/event rejects anything else, and the union below makes a
 * typo a compile error instead of a silently vanished event.
 *
 * 'trial_signup' is deliberately absent from the call sites in this repo: a real
 * registration happens on app/hrms/assessments and reaches us through the
 * registration-webhook edge function. 'demo_request' is written server-side by
 * create-demo-booking for the same reason -- it must not depend on the tab staying
 * open.
 */

import { getDeviceType, getSessionId } from "@/lib/visitorIdentity";
import { isAdminPath } from "@/utils/ctaTracking";
import type { CONVERSION_TYPE_IDS } from "@/lib/trackingTaxonomy";

export type ConversionType = (typeof CONVERSION_TYPE_IDS)[number];

/**
 * Record a completed conversion.
 *
 * Call only on genuine success. In particular do NOT call it for an
 * already-subscribed / duplicate submit: the funnel counts conversions, and a
 * re-submit by the same person is not a second one. Inflating this is how a
 * conversion rate quietly becomes fiction.
 */
export function trackConversion(
  type: ConversionType,
  action: string,
  metadata?: Record<string, string | undefined>,
): void {
  if (typeof window === "undefined") return;
  if (isAdminPath(window.location.pathname)) return;

  try {
    fetch("/api/track/event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        // Registration-shaped stages use their own step names; a secondary conversion
        // is still a completion of its own flow, which is what the funnel's last stage
        // means.
        step: "completion",
        action,
        conversion_type: type,
        session_id: getSessionId(),
        device_type: getDeviceType(),
        metadata: {
          page: window.location.pathname,
          ...metadata,
        },
      }),
      keepalive: true,
    }).catch(() => {
      /* a failed analytics write must never surface to the user mid-conversion */
    });
  } catch {
    /* nor may a synchronous throw (e.g. no fetch) */
  }
}
